/**
 * Istanza axios condivisa: TUTTE le chiamate HTTP del gestionale passano da qui
 * (upload compresi). Unico punto per baseURL, Authorization, refresh del token
 * e gestione errori.
 *
 * Flusso token:
 *  - request: se l'access token è scaduto e il refresh è ancora valido, lo
 *    rinnova PRIMA di inviare (un solo refresh in volo anche con N chiamate
 *    concorrenti); se anche il refresh è scaduto -> logout.
 *  - response 401: un solo tentativo di refresh + replay della chiamata; se
 *    fallisce -> logout (gestito da httpErrors).
 */
import axios from 'axios';
import { API_BASE_URL } from './config';
import { handleHttpError } from './httpErrors';
import {
  applyTokens,
  forceLogout,
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
  isRefreshTokenExpired,
} from './session';

const API = axios.create({ baseURL: API_BASE_URL });

const isAuthUrl = (url = '') => /\/auth\/(login|refresh|reset-password)/.test(url);

// ---- refresh single-flight -------------------------------------------------
let refreshInFlight = null;

/** Rinnova i token usando il refresh token. Istanza axios "nuda" per non
 *  rientrare negli interceptor; la baseURL è comunque quella condivisa. */
function refreshTokens() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .get('/auth/refresh', {
        baseURL: API_BASE_URL,
        headers: { Authorization: `Bearer ${getRefreshToken()}` },
      })
      .then((res) => applyTokens(res.data))
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// ---- request ---------------------------------------------------------------
API.interceptors.request.use(async (config) => {
  if (isAuthUrl(config.url)) return config;

  if (getAccessToken() && isAccessTokenExpired()) {
    if (isRefreshTokenExpired()) {
      await forceLogout();
      return Promise.reject(new axios.CanceledError('Sessione scaduta'));
    }
    try {
      await refreshTokens();
    } catch (e) {
      // lasciamo partire la chiamata: il 401 di risposta farà il logout
      console.warn('Refresh token fallito', e?.response?.status);
    }
  }

  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- response --------------------------------------------------------------
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // richiesta annullata da noi (sessione scaduta): niente toast
    if (axios.isCancel(error)) return Promise.reject(error);

    const config = error?.config;
    const status = error?.response?.status;

    // 401 inatteso (es. token revocato lato server): un tentativo di refresh + replay
    if (status === 401 && config && !config._retried && !isAuthUrl(config.url) && getRefreshToken() && !isRefreshTokenExpired()) {
      config._retried = true;
      try {
        await refreshTokens();
        config.headers.Authorization = `Bearer ${getAccessToken()}`;
        return API.request(config);
      } catch (e) {
        // refresh fallito: prosegue verso handleHttpError -> logout
      }
    }
    return handleHttpError(error);
  },
);

export default API;
